import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isSignInWithEmailLink, signInWithEmailLink } from "firebase/auth";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { auth, FIREBASE_READY } from "../lib/firebase";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { applyPendingRegistration } from "../components/AuthModal";
import { Spinner } from "../components/common";

/* /auth/complete — finishes passwordless email-link sign-in. */
export default function AuthComplete() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [needEmail, setNeedEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const complete = async (addr) => {
    try {
      await signInWithEmailLink(auth, addr, window.location.href);
      localStorage.removeItem("tp_link_email");
      localStorage.setItem("tp_last_activity", String(Date.now()));
      await applyPendingRegistration();
      try {
        if (auth?.currentUser) localStorage.setItem("token", await auth.currentUser.getIdToken());
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setError("Signed in, but the server couldn't verify your session. Please try again shortly.");
        return;
      }
      toast.success("Signed in");
      // Return to wherever the sign-in was initiated from (never hardcode /dashboard)
      const stashed = sessionStorage.getItem("tp_post_signin_return") || "/";
      sessionStorage.removeItem("tp_post_signin_return");
      nav(stashed);
    } catch (e) {
      setError(e?.code?.replace("auth/", "").replace(/-/g, " ") || "This sign-in link is invalid or expired.");
    }
  };

  useEffect(() => {
    if (!FIREBASE_READY || !isSignInWithEmailLink(auth, window.location.href)) { nav("/login"); return; }
    const stored = localStorage.getItem("tp_link_email");
    if (stored) complete(stored);
    else setNeedEmail(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return (
    <div className="max-w-md mx-auto px-5 py-24 text-center" data-testid="auth-complete-error">
      <p className="font-heading font-900 text-xl mb-2">Sign-in link problem</p>
      <p className="text-sm text-muted-foreground mb-4">{error}</p>
      <button onClick={() => nav("/login")} className="bg-primary text-white px-5 py-2.5 text-sm font-700">Back to sign in</button>
    </div>
  );

  if (needEmail) return (
    <div className="max-w-md mx-auto px-5 py-24" data-testid="auth-complete-confirm">
      <h1 className="font-heading font-900 text-2xl mb-2 flex items-center gap-2"><Mail className="w-6 h-6 text-primary" /> Confirm your email</h1>
      <p className="text-sm text-muted-foreground mb-4">For your security, re-enter the email address this sign-in link was sent to.</p>
      <form onSubmit={(e) => { e.preventDefault(); complete(email.trim()); }} className="space-y-3">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="auth-complete-email"
          className="w-full border border-zinc-300 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary" placeholder="you@example.com" />
        <button type="submit" data-testid="auth-complete-submit" className="w-full bg-primary text-white py-3 text-sm font-700 hover:bg-blue-700">Finish signing in</button>
      </form>
    </div>
  );

  return <Spinner full />;
}
